import { type SelectProps } from 'antd';
import type { BaseSelectRef } from 'rc-select';
import {
  forwardRef,
  useEffect,
  useRef,
  type ComponentType,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
  type RefAttributes
} from 'react';

import { buildAutoSelectValue, isValueSet } from './util';

type Props = {
  /** When true, enables auto-selection of the only available option */
  autoSelectEnabled?: boolean;
};

/**
 * HOC that auto-selects the only available option when:
 * - autoSelectEnabled is true (default)
 * - options.length === 1
 * - AND no value is currently selected
 * - AND the select is not disabled or loading
 *
 * The returned component extends SelectProps so it's a drop-in replacement
 * for AntD Select — no type casting needed at consumer sites.
 *
 * Works with default, multiple, and tags modes.
 * Respects labelInValue. Does NOT override existing selections.
 *
 * @example
 * const SmartSelect = withAutoSelectSingleOption(Select);
 * <SmartSelect autoSelectEnabled options={[{ value: '1', label: 'Only' }]} onChange={fn} />
 */
const withAutoSelectSingleOption = <
  R = BaseSelectRef,
  P extends SelectProps = SelectProps
>(
  WrappedComponent: ComponentType<P>
): ForwardRefExoticComponent<PropsWithoutRef<P & Props> & RefAttributes<R>> => {
  const AutoSelectComponent = forwardRef<R, P & Props>((props, ref) => {
    const { autoSelectEnabled = true, ...wrappedProps } = props;

    const { options, value, onChange, mode, labelInValue, disabled, loading } =
      wrappedProps;

    // Guard ref to ensure auto-select fires only once per options identity.
    // Reset when options reference changes so new async loads trigger re-evaluation.
    const hasAutoSelectedRef = useRef(false);
    const prevOptionsRef = useRef(options);

    // Reset the guard when options identity changes (e.g. async fetch resolves)
    useEffect(() => {
      if (prevOptionsRef.current !== options) {
        hasAutoSelectedRef.current = false;
        prevOptionsRef.current = options;
      }
    }, [options]);

    useEffect(() => {
      if (
        hasAutoSelectedRef.current ||
        !autoSelectEnabled ||
        disabled ||
        loading ||
        options?.length !== 1 ||
        !onChange ||
        isValueSet(value)
      ) {
        return;
      }

      const option = options[0];

      if (option.disabled) {
        return;
      }

      hasAutoSelectedRef.current = true;

      const isMultiMode = mode === 'multiple' || mode === 'tags';
      const autoValue = buildAutoSelectValue(option, mode, labelInValue);

      // AntD onChange signature: (value, option | option[])
      // In multi-mode the second argument must be an array of options
      onChange(autoValue, isMultiMode ? [option] : option);
    }, [
      options,
      value,
      onChange,
      mode,
      labelInValue,
      autoSelectEnabled,
      disabled,
      loading
    ]);

    return <WrappedComponent ref={ref} {...(wrappedProps as unknown as P)} />;
  });

  const wrappedName =
    (WrappedComponent as ComponentType).displayName ||
    (WrappedComponent as ComponentType).name ||
    'Component';
  AutoSelectComponent.displayName = `withAutoSelectSingleOption(${wrappedName})`;

  return AutoSelectComponent;
};

export default withAutoSelectSingleOption;
